import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { GradientButton } from '../../components/common/Primitives';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { SCHOOL_REQUEST_DEMO } from '../../lib/mockSchool';
import { submitForm } from '../../lib/api';
import { Calendar, CheckCircle2 } from 'lucide-react';

const inputCls = 'w-full rounded-lg border border-canvas-200 bg-white px-4 py-2.5 text-[14px] text-navy placeholder:text-ink-500/60 focus:outline-none focus:ring-2 focus:ring-purple/40';
const labelCls = 'text-[13px] font-semibold text-ink-700';

export default function SchoolRequestDemo() {
  const d = SCHOOL_REQUEST_DEMO;
  const [form, setForm] = useState({
    school: '', type: '', city: '', website: '',
    contact: '', role: '', email: '', phone: '',
    demo: 'online', date: '', time: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true); setSubmitError(null);
    try {
      await submitForm({
        formType: 'school-request-demo',
        fields: form,
        contactEmail: form.email,
        contactName: form.contact,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PageShell>
        <section className="px-6 py-24" data-testid="school-demo-confirmation">
          <div className="mx-auto max-w-[720px]">
            <div className="rounded-3xl border border-mint/30 bg-gradient-to-br from-mint-50 via-cyan-50 to-purple-50 p-10 shadow-soft text-center">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-mint text-white shadow"><CheckCircle2 className="h-8 w-8" /></div>
              <h2 className="mt-5 text-[26px] md:text-[30px] font-extrabold text-navy">{d.confirmation.title}</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-700 max-w-lg mx-auto">{d.confirmation.body}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <GradientButton to="/school">Back to Overview</GradientButton>
                <GradientButton to="/school/start-deployment" variant="outline">Start Deployment</GradientButton>
              </div>
            </div>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="school-demo-hero">
        <div className="mx-auto max-w-[1240px]">
          <h1 className="text-[40px] md:text-[52px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
          <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
          <div className="mt-6"><LicenceFeeHighlight /></div>
        </div>
      </section>

      {/* FORM */}
      <section className="px-6 py-8 pb-24">
        <div className="mx-auto max-w-[960px] rounded-3xl border border-canvas-200 bg-white p-6 md:p-10 shadow-soft">
          <h2 className="text-[22px] font-extrabold text-navy">{d.formTitle}</h2>
          <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 gap-5" data-testid="school-demo-form">
            {/* Row 1 — school name + type */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="d-school">School or institution name</label>
                <input id="d-school" required data-testid="demo-school-name" value={form.school} onChange={e => set('school', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter institution name" />
              </div>
              <div>
                <label className={labelCls} htmlFor="d-type">School type</label>
                <select id="d-type" required data-testid="demo-school-type" value={form.type} onChange={e => set('type', e.target.value)} className={`mt-2 ${inputCls}`}>
                  <option value="">Select school type</option>
                  {d.schoolTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            {/* Row 2 */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="d-city">City</label>
                <input id="d-city" required data-testid="demo-city" value={form.city} onChange={e => set('city', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter city" />
              </div>
              <div>
                <label className={labelCls} htmlFor="d-website">Website</label>
                <input id="d-website" type="url" data-testid="demo-website" value={form.website} onChange={e => set('website', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="https://..." />
              </div>
            </div>
            {/* Row 3 */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="d-contact">Contact person</label>
                <input id="d-contact" required data-testid="demo-contact" value={form.contact} onChange={e => set('contact', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Full name" />
              </div>
              <div>
                <label className={labelCls} htmlFor="d-role">Role or designation</label>
                <select id="d-role" required data-testid="demo-role" value={form.role} onChange={e => set('role', e.target.value)} className={`mt-2 ${inputCls}`}>
                  <option value="">Select role</option>
                  {d.roles.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            {/* Row 4 */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="d-email">Email</label>
                <input id="d-email" type="email" required data-testid="demo-email" value={form.email} onChange={e => set('email', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="name@school.org" />
              </div>
              <div>
                <label className={labelCls} htmlFor="d-phone">Phone</label>
                <input id="d-phone" required data-testid="demo-phone" value={form.phone} onChange={e => set('phone', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="+91" />
              </div>
            </div>
            {/* Demo preference */}
            <div>
              <div className={labelCls}>Demo preference</div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {d.demoOptions.map(opt => (
                  <label key={opt.key} className={`cursor-pointer rounded-xl border p-4 shadow-soft transition-colors ${form.demo === opt.key ? 'border-purple bg-purple-50' : 'border-canvas-200 bg-white hover:border-purple/40'}`} data-testid={`demo-preference-${opt.key}`}>
                    <input type="radio" name="demo" value={opt.key} checked={form.demo === opt.key} onChange={e => set('demo', e.target.value)} className="sr-only" />
                    <div className="text-[14px] font-semibold text-navy">{opt.label}</div>
                    {opt.note && <div className="mt-1 text-[11.5px] text-ink-500">{opt.note}</div>}
                  </label>
                ))}
              </div>
            </div>
            {/* Date + Time */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="d-date">Preferred date</label>
                <div className="relative mt-2">
                  <input id="d-date" type="date" data-testid="demo-date" value={form.date} onChange={e => set('date', e.target.value)} className={inputCls} />
                  <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="d-time">Preferred time</label>
                <select id="d-time" data-testid="demo-time" value={form.time} onChange={e => set('time', e.target.value)} className={`mt-2 ${inputCls}`}>
                  <option value="">Select a time window</option>
                  {d.timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            {/* Message */}
            <div>
              <label className={labelCls} htmlFor="d-msg">Message</label>
              <textarea id="d-msg" data-testid="demo-message" rows={3} value={form.message} onChange={e => set('message', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Optional: tell us what you would like to see in the demo" />
            </div>
            {/* Submit */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button type="submit" disabled={submitting} data-testid="demo-submit" className={`inline-flex items-center justify-center rounded-lg px-6 py-3 text-[14px] font-semibold text-white shadow-soft ${submitting ? 'bg-canvas-200 text-ink-500 cursor-not-allowed' : 'bg-brand-gradient hover:brightness-110'}`}>{submitting ? 'Submitting…' : d.submit}</button>
              <span className="text-[12.5px] text-ink-500">{d.submitHint}</span>
            </div>
            {submitError && (
              <div className="mt-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[12.5px] text-red-700" data-testid="demo-submit-error">{submitError}</div>
            )}
          </form>
        </div>
      </section>
    </PageShell>
  );
}
